import React, { useState, useEffect } from 'react';
import { Box, Grid, TextField, Typography, FormHelperText, Button } from '@mui/material';
import {
  validateTitle,
  validateSubtitle,
  validateMetaTitle,
  validateMetaDescription,
  getMetaTitleStatus,
  getMetaDescriptionStatus
} from './setting-validation';
import SaveIcon from '@mui/icons-material/Save';
import palette from 'src/theme/palette';
import { postSettingData } from 'src/ApiServices';
import { toast } from 'react-toastify';

const HomepageSEOTab = ({ tabData = [], typeId }) => {
  // Initialize formData with id and value structure
  const getInitialFormData = () => {
    const dataMap = {};
    tabData.forEach((item) => {
      dataMap[item.key] = {
        id: item.id || '',
        value: item.value || ''
      };
    });

    return {
      website_title: dataMap['website_title'] || { id: '', value: '' },
      website_subtitle: dataMap['website_subtitle'] || { id: '', value: '' },
      website_meta_title: dataMap['website_meta_title'] || { id: '', value: '' },
      website_meta_description: dataMap['website_meta_description'] || { id: '', value: '' }
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Update formData when tabData changes
  useEffect(() => {
    setFormData(getInitialFormData());
  }, [tabData]);

  const handleSaveSettings = async () => {
    const valueArray = [
      {
        id: formData.website_title.id || null,
        key: 'website_title',
        value: formData.website_title.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Homepage SEO',
        created_at: formData.website_title.id ? '2025-11-21 06:49:24' : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.website_subtitle.id || null,
        key: 'website_subtitle',
        value: formData.website_subtitle.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Homepage SEO',
        created_at: formData.website_subtitle.id ? '2025-11-21 06:49:24' : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.website_meta_title.id || null,
        key: 'website_meta_title',
        value: formData.website_meta_title.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Homepage SEO',
        created_at: formData.website_meta_title.id ? '2025-11-21 06:49:24' : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.website_meta_description.id || null,
        key: 'website_meta_description',
        value: formData.website_meta_description.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Homepage SEO',
        created_at: formData.website_meta_description.id ? '2025-11-21 06:49:24' : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      }
    ];

    const finalPayload = {
      type_id: typeId.toString(),
      value: JSON.stringify(valueArray)
    };

    try {
      console.log('Final Payload:', finalPayload);

      // Call the API
      const response = await postSettingData(finalPayload);

      // Check if response exists and has data
      if (response) {
        // Handle based on status code
        const status = response.status || response.statusCode || 200;
        const data = response.data || response;
        const message = response.message || data.message || 'SEO settings saved successfully!';

        if (status >= 200 && status < 300) {
          // Success - show exact response message
          toast.success(message, {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          });
          console.log('Success Response:', data);
        } else if (status === 401 || status === 403) {
          // Authentication/Authorization errors
          toast.error(message || 'Unauthorized access. Please login again.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          });
        } else if (status === 400) {
          // Validation errors
          const validationMsg = message || 'Please check your input data.';
          toast.error(validationMsg, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          });
        } else if (status === 404) {
          // Not found
          toast.error(message || 'Resource not found.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          });
        } else if (status >= 500) {
          // Server errors
          toast.error(message || 'Server error. Please try again later.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          });
        } else {
          // Other errors
          toast.error(message || 'Something went wrong. Please try again.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          });
        }
      } else {
        // No response object
        toast.error('No response received from server.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
    } catch (error) {
      // Network errors or exceptions
      console.error('API Error:', error);

      const errorMessage =
        error.message ||
        (error.response && error.response.data && error.response.data.message) ||
        'Network error. Please check your connection.';

      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setLoading(false);
    }
  };
  // Function to trim text to maximum length
  // const trimToMaxLength = (value, maxLength) => {
  //   return value.length > maxLength ? value.substring(0, maxLength) : value;
  // };

  // const handleInputChange = (field, value) => {
  //   let trimmedValue = value;

  //   // Apply character limits based on field
  //   switch (field) {
  //     case 'website_title':
  //       trimmedValue = trimToMaxLength(value, 20);
  //       break;
  //     case 'website_subtitle':
  //       trimmedValue = trimToMaxLength(value, 60);
  //       break;
  //     case 'website_meta_title':
  //       trimmedValue = trimToMaxLength(value, 120);
  //       break;
  //     case 'website_meta_description':
  //       trimmedValue = trimToMaxLength(value, 160);
  //       break;
  //     default:
  //       break;
  //   }

  //   setFormData((prev) => ({
  //     ...prev,
  //     [field]: {
  //       ...prev[field],
  //       value: trimmedValue
  //     }
  //   }));

  //   // Validate after trimming
  //   let error = '';
  //   switch (field) {
  //     case 'website_title':
  //       error = validateTitle(trimmedValue);
  //       break;
  //     case 'website_subtitle':
  //       error = validateSubtitle(trimmedValue);
  //       break;
  //     case 'website_meta_title':
  //       error = validateMetaTitle(trimmedValue);
  //       break;
  //     case 'website_meta_description':
  //       error = validateMetaDescription(trimmedValue);
  //       break;
  //     default:
  //       break;
  //   }

  //   setErrors((prev) => ({ ...prev, [field]: error }));
  // };

  // // Handle paste event to prevent exceeding limits
  // const handlePaste = (e, maxLength) => {
  //   const pastedText = e.clipboardData.getData('text');
  //   if (pastedText.length > maxLength) {
  //     e.preventDefault();
  //     const trimmedText = pastedText.substring(0, maxLength);
  //     document.execCommand('insertText', false, trimmedText);
  //   }
  // };

  const metaTitleStatus = getMetaTitleStatus(formData.website_meta_title.value);
  const metaDescStatus = getMetaDescriptionStatus(formData.website_meta_description.value);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value: value
      }
    }));
  };

  return (
    <Box component="form" noValidate>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Website Hero Section Title"
            name="website_title"
            value={formData?.website_title?.value || ''}
            onChange={(e) => handleInputChange('website_title', e.target.value)}
            // onPaste={(e) => handlePaste(e, 20)}
            placeholder="Enter hero section title (max 20 characters)"
            error={!!errors.website_title}
            helperText={
              errors.website_title || `${formData.website_title.value.length}/20 characters`
            }
            inputProps={{ maxLength: 20 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Website Hero Section Subtitle"
            name="website_subtitle"
            value={formData?.website_subtitle?.value || ''}
            onChange={(e) => handleInputChange('website_subtitle', e.target.value)}
            // onPaste={(e) => handlePaste(e, 60)}
            placeholder="Enter hero section subtitle (max 60 characters)"
            error={!!errors.website_subtitle}
            helperText={
              errors.website_subtitle || `${formData.website_subtitle.value.length}/60 characters`
            }
            inputProps={{ maxLength: 60 }}
          />
        </Grid>
        <Grid item xs={12}>
          {/* <TextField
            fullWidth
            label="Website Meta Title (Homepage)"
            name="website_meta_title"
            value={formData?.website_meta_title?.value || ''}
            onChange={(e) => handleInputChange('website_meta_title', e.target.value)}
            onPaste={(e) => handlePaste(e, 120)}
            placeholder="Enter meta title for homepage (max 120 characters)"
            error={!!errors.website_meta_title}
            helperText={
              errors.website_meta_title ||
              `${formData.website_meta_title.value.length}/120 characters`
            }
            inputProps={{ maxLength: 120 }}
          /> */}
          <TextField
            fullWidth
            label="Website Meta Title (Homepage)"
            name="website_meta_title"
            value={formData.website_meta_title.value}
            onChange={(e) => handleInputChange('website_meta_title', e.target.value)}
            placeholder="Home For Sale in Pittsburgh PA | YourBrand"
            helperText={
              <>
                <Typography
                  variant="caption"
                  color={
                    metaTitleStatus.color === 'success'
                      ? 'success.main'
                      : metaTitleStatus.color === 'warning'
                      ? 'warning.main'
                      : metaTitleStatus.color === 'error'
                      ? 'error.main'
                      : 'text.secondary'
                  }
                >
                  {metaTitleStatus.message}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  • Ideal length: 50–60 characters • Use Title Case • Include keywords (e.g. “Home
                  For Sale”, “Pittsburgh PA”) • End with brand/company name
                </Typography>
              </>
            }
          />
        </Grid>
        <Grid item xs={12}>
          {/* <TextField
            fullWidth
            multiline
            minRows={3}
            label="Website Meta Description (Homepage)"
            name="website_meta_description"
            value={formData?.website_meta_description?.value || ''}
            onChange={(e) => handleInputChange('website_meta_description', e.target.value)}
            onPaste={(e) => handlePaste(e, 160)}
            placeholder="Enter meta description for homepage (120-160 characters)"
            error={!!errors.website_meta_description}
            helperText={
              errors.website_meta_description ||
              (formData.website_meta_description.value.length < 120
                ? `${formData.website_meta_description.value.length}/160 characters - Minimum 120 recommended`
                : `${formData.website_meta_description.value.length}/160 characters`)
            }
            inputProps={{ maxLength: 160 }}
          /> */}

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Website Meta Description (Homepage)"
            name="website_meta_description"
            value={formData.website_meta_description.value}
            onChange={(e) => handleInputChange('website_meta_description', e.target.value)}
            placeholder="Find homes for sale in Pittsburgh PA with expert local agents..."
            helperText={
              <Typography
                variant="caption"
                color={
                  metaDescStatus.color === 'success'
                    ? 'success.main'
                    : metaDescStatus.color === 'warning'
                    ? 'warning.main'
                    : metaDescStatus.color === 'error'
                    ? 'error.main'
                    : 'text.secondary'
                }
              >
                {metaDescStatus.message}
              </Typography>
            }
          />
        </Grid>

        <Grid item xs={12} container justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              '&:hover': { background: palette.primary.dark }
            }}
          >
            Save Settings
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HomepageSEOTab;
